import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import tr from 'locale';

const Page = styled.div`
  display: flex;
  min-height: 100vh;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  background: ${({ theme }) =>
    (theme.background && theme.background.secondary) ||
    theme.backgroundPrimaryColor ||
    '#f5f6f8'};
`;

const Card = styled.div`
  background: ${({ theme }) =>
    (theme.background && theme.background.default) ||
    theme.backgroundColor ||
    '#ffffff'};
  border-radius: 6px;
  padding: 28px 32px 30px;
  max-width: 460px;
  width: 100%;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.12);
  border: ${({ theme }) =>
    (theme.border && theme.border.default) ||
    theme.border ||
    '1px solid #e5e7eb'};
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 16px 0;
  text-align: center;
  color: ${({ theme }) =>
    (theme.text && theme.text.default) || theme.mainColor || '#111827'};
`;

const InfoText = styled.div`
  font-size: 13px;
  margin-bottom: 20px;
  text-align: center;
  color: ${({ theme }) =>
    (theme.text && theme.text.secondary) || theme.grayColor || '#6b7280'};
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  margin-bottom: 6px;
  font-weight: 500;
  color: ${({ theme }) =>
    (theme.text && theme.text.secondary) || '#4b5563'};
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 9px 10px;
  border-radius: 4px;
  font-size: 13px;
  border: ${({ theme }) =>
    (theme.border && theme.border.default) ||
    theme.border ||
    '1px solid #d1d5db'};
  background: ${({ theme }) =>
    (theme.background && theme.background.input) ||
    theme.whiteColor ||
    '#ffffff'};
  color: ${({ theme }) =>
    (theme.text && theme.text.default) || theme.mainColor || '#111827'};

  &:focus {
    outline: none;
    border-color: ${({ theme }) =>
      (theme.button && theme.button.primary) ||
      theme.primaryColor ||
      'hsla(120, 45%, 56%, 1)'};
    box-shadow: 0 0 0 1px
      ${({ theme }) =>
        (theme.button && theme.button.primaryShadow) ||
        'rgba(34, 197, 94, 0.25)'};
  }
`;

const Button = styled.button`
  margin-top: 18px;
  width: 100%;
  padding: 10px 12px;
  border-radius: 4px;
  border: none;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  background: ${({ theme }) =>
    (theme.button && theme.button.primary) ||
    theme.primaryColor ||
    'hsla(120, 45%, 56%, 1)'};
  color: ${({ theme }) =>
    (theme.button && theme.button.primaryText) ||
    theme.whiteColor ||
    '#ffffff'};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  transition: box-shadow 0.08s ease, transform 0.05s ease;

  &:hover {
    box-shadow: 0 10px 20px
      ${({ theme }) =>
        (theme.button && theme.button.primaryShadow) ||
        'rgba(22, 163, 74, 0.35)'};
    transform: translateY(-1px);
  }

  &:active {
    box-shadow: none;
    transform: translateY(0);
  }
`;

const ErrorText = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: ${({ theme }) =>
    (theme.text && theme.text.error) || theme.errorColor || '#b91c1c'};
`;

const emailRegExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function NuitrackCheckout() {
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const priceId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('price_id') || '';
  }, [location.search]);

  const successUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('success_url') || '';
  }, [location.search]);

  const cancelUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('cancel_url') || '';
  }, [location.search]);

  const isEmailValid = emailRegExp.test(email);

  const handleSubmit = async event => {
  event.preventDefault();
  setTouched(true);
  setError('');

  if (!priceId) {
    setError(tr('checkout.errors.noPriceId'));
    return;
  }

  if (!isEmailValid) {
    setError(tr('checkout.errors.emailInvalidFull'));
    return;
  }

  setLoading(true);

  try {
    const params = new URLSearchParams();
    params.set('price_id', priceId);
    params.set('email', email);

    if (successUrl) {
      params.set('success_url', successUrl);
    }

    if (cancelUrl) {
      params.set('cancel_url', cancelUrl);
    }

    const query = params.toString();

    const response = await fetch(`/payments/create-checkout-session/?${query}`);

    if (!response.ok) {
      throw new Error(tr('checkout.errors.httpPrefix') + response.status);
    }

    const data = await response.json();

    const sessionUrl =
      data &&
      data.payment &&
      data.payment.provider_data &&
      data.payment.provider_data.session_url;

    if (!sessionUrl) {
      throw new Error(tr('checkout.errors.noSessionUrl'));
    }

    window.location.href = sessionUrl;

  } catch (e) {
    console.error(e);
    setError(e.message || tr('checkout.errors.unknown'));
    setLoading(false);
  }
};

  return (
    <Page>
      <Card>
        <Title>{tr('checkout.title')}</Title>

        <InfoText>
          {tr('checkout.info.line1')}
          <br />
          {tr('checkout.info.line2')}
        </InfoText>

        {!priceId && <ErrorText>{tr('checkout.errors.urlPriceId')}</ErrorText>}

        <form onSubmit={handleSubmit}>
          <Label htmlFor="email">{tr('checkout.email.label')}</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder={tr('checkout.email.placeholder')}
          />

          {touched && !isEmailValid && email.length > 0 && (
            <ErrorText>{tr('checkout.errors.emailInvalidShort')}</ErrorText>
          )}

          {error && <ErrorText>{error}</ErrorText>}

          <Button type="submit" disabled={!isEmailValid || !priceId || loading}>
            {loading ? tr('checkout.button.loading') : tr('checkout.button.next')}
          </Button>
        </form>
      </Card>
    </Page>
  );
}

export default NuitrackCheckout;
